import React, { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  shouldPromptDesktopNotifs,
  dismissDesktopNotifsPrompt,
  requestDesktopNotifsPermission,
} from "@/lib/desktopNotifs";

/**
 * Bottom-right nudge that offers to turn on browser notifications so users
 * still hear huddle/DM pings when TOVRA's tab isn't focused. Only appears
 * when permission is "default" (never asked) and the user hasn't recently
 * dismissed. Waits 8s after mount so it doesn't hijack the first paint.
 */
export default function DesktopNotifsPrompt() {
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (shouldPromptDesktopNotifs()) setShow(true);
    }, 8000);
    return () => clearTimeout(timer);
  }, []);

  const enable = async () => {
    setBusy(true);
    const result = await requestDesktopNotifsPermission();
    setBusy(false);
    setShow(false);
    if (result === "granted") {
      toast.success("Desktop notifications on. We'll ping you for huddles.");
    } else if (result === "denied") {
      toast("Notifications blocked. You can re-enable them in your browser settings.");
    }
  };

  const dismiss = () => {
    dismissDesktopNotifsPrompt();
    setShow(false);
  };

  if (!show) return null;
  return (
    <div
      className="fixed bottom-4 right-4 z-40 w-[300px] rounded-xl border border-zinc-200 bg-white shadow-xl p-3 huddle-banner-pop"
      data-testid="desktop-notifs-prompt"
      role="dialog"
    >
      <div className="flex items-start gap-2.5">
        <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center shrink-0">
          <Bell className="w-4 h-4 text-[var(--brand-accent)]"/>
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-zinc-900">Get notified for huddles</div>
          <p className="text-[11px] text-zinc-500 mt-0.5 leading-snug">
            Even when TOVRA isn&apos;t your active tab, we&apos;ll ping you when a teammate calls.
          </p>
          <div className="mt-2 flex items-center gap-2">
            <Button
              size="sm"
              onClick={enable}
              disabled={busy}
              data-testid="desktop-notifs-enable"
              className="h-7 px-3 bg-[var(--brand-accent)] hover:bg-orange-700 text-white text-xs"
            >
              {busy ? "Waiting…" : "Enable"}
            </Button>
            <button
              onClick={dismiss}
              data-testid="desktop-notifs-dismiss"
              className="text-[11px] text-zinc-500 hover:text-zinc-800"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="p-1 -m-1 text-zinc-400 hover:text-zinc-700"
          aria-label="Dismiss"
        >
          <X className="w-3.5 h-3.5"/>
        </button>
      </div>
    </div>
  );
}
